/**
 * Shared FAB sizing for the Floating Actions subsystem.
 *
 * Only values that must stay identical across every circular FAB touch
 * target (GPS, burst main button, sub-FAB wrappers) belong here, plus the
 * shared autocomplete fade for the two primary FABs. Burst-only timing,
 * safe-areas, long-press thresholds and sub-FAB outer margin for arc
 * geometry live in `FloatingActionBurst/floatingActionBurst.configuration.ts`.
 *
 * Every constant below documents its unit, its intent and when tuning it
 * makes sense.
 */

/**
 * Diameter (px) of the primary FABs visible at rest (the GPS FAB and the
 * burst main FAB). Changing this value rescales both simultaneously so
 * they never drift apart visually. Tune it when the overall map header
 * density changes.
 */
export const PRIMARY_FAB_SIZE_PX = 40;

/**
 * Diameter (px) of each sub-FAB rendered inside the burst. Intentionally
 * matches `PRIMARY_FAB_SIZE_PX` so the expanded ring remains visually
 * coherent with the main FAB.
 */
export const SUB_FAB_SIZE_PX = 40;

/**
 * Size (px) of the icon image rendered inside every FAB wrapper (primary
 * and sub). Kept as a single value so icons stay visually balanced across
 * the two visible FABs and every sub-FAB.
 */
export const FAB_ICON_SIZE_PX = 32;

/**
 * Icon size (px) for vehicle sub-FABs (bike, car). Slightly below
 * `FAB_ICON_SIZE_PX` because transport-mode SVGs read large inside the 40px ring.
 */
export const BURST_SUB_FAB_VEHICLE_ICON_SIZE_PX = 24;

/**
 * CSS `filter` that recolours any burst sub-FAB asset to brand primary green
 * (`#11D39C`), matching MapStyle and pricing icons. `brightness(0)` first
 * normalises coloured sources (red car, green bike) before the green tint.
 */
export const BURST_SUB_FAB_GREEN_FILTER =
    "brightness(0) saturate(100%) invert(61%) sepia(57%) saturate(686%) hue-rotate(110deg) brightness(92%) contrast(88%)";

/**
 * Opacity for both primary FABs (GPS + burst root) while a search
 * autocomplete panel is open, so they stay visible but unobtrusive.
 */
export const SEARCH_AUTOCOMPLETE_PRIMARY_FAB_OPACITY = 0.15;

/**
 * Duration (ms) of the opacity transition when search autocomplete opens or
 * closes. Kept identical for GPS and burst.
 */
export const SEARCH_AUTOCOMPLETE_PRIMARY_FAB_FADE_MS = 100;
